/**
 * Remove one user-defined row, from the row itself.
 *
 * Two steps, inline: the × arms a confirm that names nothing new but has to be
 * pressed on purpose. A definition is small to recreate but its removal is
 * silent and immediate — the whole row leaves the grid — so a single stray
 * click on a hover-revealed control is the wrong cost.
 *
 * Kept out of `window.confirm` deliberately: a native dialog blocks the page
 * and reads as a browser event rather than part of the table.
 */

import { X } from "lucide-react";
import React, { useCallback, useState } from "react";

export interface KpiCustomMetricDeleteProps {
	/** The row's key — handed back to `onDelete` so the grid can pass ONE
	 * stable callback for every row instead of a fresh closure each render,
	 * which would defeat the memo below. */
	metricKey: string;
	/** What is being removed — named in the control's accessible label. */
	metricLabel: string;
	onDelete: (metricKey: string) => void;
	/** True while the delete and its refetch are in flight. */
	busy?: boolean;
}

const KpiCustomMetricDelete: React.FC<KpiCustomMetricDeleteProps> = ({
	metricKey,
	metricLabel,
	onDelete,
	busy = false,
}) => {
	const [armed, setArmed] = useState(false);

	const arm = useCallback(() => setArmed(true), []);
	const cancel = useCallback(() => setArmed(false), []);
	const confirm = useCallback(() => {
		setArmed(false);
		onDelete(metricKey);
	}, [metricKey, onDelete]);

	if (armed) {
		return (
			<span className="ml-2 inline-flex items-center gap-1 align-middle">
				<button
					type="button"
					onClick={confirm}
					disabled={busy}
					className="rounded border border-red-300 px-1.5 py-0.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
				>
					{busy ? "Removing…" : "Remove"}
				</button>
				<button
					type="button"
					onClick={cancel}
					className="rounded border border-gray-300 px-1.5 py-0.5 text-xs text-text-muted hover:bg-gray-50"
				>
					Cancel
				</button>
			</span>
		);
	}

	return (
		<button
			type="button"
			onClick={arm}
			aria-label={`Remove ${metricLabel}`}
			// Revealed on row hover, but always reachable from the keyboard: an
			// opacity-only reveal would hide a control focus can still land on.
			className="ml-2 rounded p-0.5 align-middle text-text-muted opacity-0 transition-opacity hover:bg-gray-200 focus:opacity-100 group-hover:opacity-100"
		>
			<X className="h-3 w-3" />
		</button>
	);
};

export default React.memo(KpiCustomMetricDelete);
